// src/pages/ForgotPassword.tsx
// Updated: 2026-08-20 15:00:00 CDT
// Purpose: Send a Supabase password-recovery email without revealing account existence.

import { useState } from "react";
import RouteHeader from '@/components/RouteHeader';
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { buildPasswordResetRedirect } from "@/utils/passwordSecurity";
import { Film, Mail } from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [requestCompleted, setRequestCompleted] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setErrorMessage("");

    try {
      const redirectTo = buildPasswordResetRedirect(window.location.origin);
      console.debug("[ForgotPassword] Requesting password recovery:", {
        redirectTo,
        email: "***redacted***",
      });

      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo,
      });

      if (error) {
        throw error;
      }

      setRequestCompleted(true);
      console.debug("[ForgotPassword] Recovery request accepted by Supabase.");
    } catch (error) {
      console.error("[ForgotPassword] Recovery request failed:", error);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Unable to send the password recovery email.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background"><RouteHeader/><div className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center bg-linear-to-br from-background via-background to-primary/5 p-4">
      <Card className="w-full max-w-md border-primary/10 bg-card/50 backdrop-blur-sm">
        <CardHeader className="text-center">
          <div className="mb-4 flex items-center justify-center space-x-2">
            <Film className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-bold">TableRead.tv</h1>
          </div>
          <CardTitle>Reset your password</CardTitle>
          <CardDescription>
            Enter your account email and we will send a secure password-reset link.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {requestCompleted ? (
            <div className="space-y-4">
              <p className="rounded-lg border border-primary/30 bg-primary/10 p-3 text-sm" role="status">
                If an account exists for that address, a password-reset email is on its way.
                Check your inbox and spam folder.
              </p>
              <Button asChild className="w-full">
                <Link to="/login">Return to sign in</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="recovery-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="recovery-email"
                    type="email"
                    autoComplete="email"
                    placeholder="writer@example.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="pl-10"
                    disabled={submitting}
                    required
                  />
                </div>
              </div>

              {errorMessage && (
                <p className="text-sm text-destructive" role="alert">
                  {errorMessage}
                </p>
              )}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Sending reset link…" : "Send reset link"}
              </Button>

              <Button asChild variant="ghost" className="w-full">
                <Link to="/login">Back to sign in</Link>
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div></div>
  );
};

export default ForgotPassword;
